import type { BUSINESS_FLOW_RULES } from './business-logic-data.js';
import type { BusinessText } from './business-logic-types.js';

export type BusinessAuthSummary = {
  title: BusinessText;
  basis: BusinessText;
  email: BusinessText;
  recovery: BusinessText;
};

export type BusinessAuthFlowId = keyof typeof BUSINESS_FLOW_RULES;

const t = (de: string, en: string): BusinessText => ({ de, en });

const newCustomerSummary = (spoken: boolean): BusinessAuthSummary => ({
  title: t('Telefon + Vor- und Nachname', 'Phone + first name + surname'),
  basis: spoken
    ? t(
        'Die angesagte Nummer und beide vollständigen Namen müssen im bestätigten Firmenkonto dieselbe eindeutige CRM-Identität ergeben. Der Nachname muss ausreichend stark passen; dann entfällt E-Mail.',
        'Matching spoken phone and both complete names must identify one unambiguous eligible CRM identity in the confirmed company. A sufficiently strong surname allows email to be omitted.',
      )
    : t(
        'Das eingehende Telefon und beide vollständigen Namen müssen im bestätigten Firmenkonto dieselbe eindeutige CRM-Identität ergeben. Der Nachname muss ausreichend stark passen; dann entfällt E-Mail.',
        'Matching incoming phone and both complete names must identify one unambiguous eligible CRM identity in the confirmed company. A sufficiently strong surname allows email to be omitted.',
      ),
  email: t(
    'Nur wenn die Namen akzeptiert sind, aber der zusätzliche Identitätsbeleg fehlt oder mehrdeutig bleibt — etwa bei einem schwachen phonetischen Nachnamen.',
    'Needed when names are accepted but additional identity evidence is missing or ambiguous — for example, a weak phonetic surname.',
  ),
  recovery: t(
    'Falsche Namen → Korrektur, bei ausgeschöpfter Prüfung → Mitarbeiter. Eine akzeptierte schwache phonetische Variante kann vor E-Mail eine zusätzliche Buchstabierchance erhalten; nur unter den geltenden Bedingungen.',
    'Incorrect names → correction; exhausted checks → colleague. An accepted weak phonetic variant can receive an extra spelling opportunity before email, subject to the existing conditions.',
  ),
});

// Concise customer-facing companion to the detailed stage/source descriptions.
// Eligibility is documented, not executed. Numeric thresholds remain in the
// shared BUSINESS_MATCHING_RULES display and the detailed policy sections.
export const BUSINESS_AUTH_SUMMARIES: Readonly<Record<BusinessAuthFlowId, BusinessAuthSummary>> = {
  a: newCustomerSummary(false),
  b: newCustomerSummary(false),
  c21: newCustomerSummary(true),
  existing: {
    title: t('CAS-Zugriff: Telefon + vollständige Namen', 'CAS access: phone + complete names'),
    basis: t(
      'Im normalen Navigate-existing können eingehendes oder angesagtes Telefon und beide Namen genügen: eine eindeutige zulässige Person mit ausreichend starkem Nachnamen.',
      'For ordinary Navigate existing-ticket, incoming or spoken phone and both names can suffice: one unambiguous eligible person with a sufficiently strong surname.',
    ),
    email: t(
      'Zusätzlich nötig, wenn die Namen akzeptiert sind, der Telefon-/Identitätsbeleg aber nicht genügt oder mehrdeutig bleibt. Keine automatische E-Mail nach falschen Namen.',
      'Additional email is needed if names are accepted but phone/identity evidence is insufficient or ambiguous. Incorrect names do not automatically lead to email.',
    ),
    recovery: t(
      'Namensfehler werden korrigiert; die Prüfgrenze führt zum Mitarbeiter. Keine besondere phonetische Zusatzchance und kein KI-E-Mail-Prüfer.',
      'Name mismatches receive correction; reaching the limit leads to a colleague. No special phonetic spelling opportunity or AI email verifier.',
    ),
  },
  callback: {
    title: t('Rückruf: eingehendes Telefon + Namen', 'Return call: incoming phone + names'),
    basis: t(
      'Ein passendes eingehendes Telefon und beide vollständigen Namen können eine eindeutige Person mit starkem Nachnamen belegen. Die neue Abkürzung für angesagte Telefonnummern gilt hier nicht.',
      'Matching incoming phone and both complete names can establish one person with a strong surname. The new shortcut for spoken phone numbers does not apply here.',
    ),
    email: t(
      'Nach akzeptierten Namen erforderlich, wenn der eingehende Telefonbeleg nicht genügt — auch beim angesagten Telefon. Ein unklarer Personenbezug darf nicht geraten werden.',
      'Required after accepted names when incoming-phone evidence is insufficient, including the spoken-phone route. An unclear identity must not be guessed.',
    ),
    recovery: t(
      'Namensfehler → Korrektur; ausgeschöpfte Prüfung → Mitarbeiter. Normale CAS-E-Mail-Prüfung, ohne besondere phonetische Zusatzchance oder KI-Prüfer. Kein Closed → New in diesem Modus.',
      'Name mismatches → correction; exhausted checks → colleague. Ordinary CAS email verification, without the special phonetic opportunity or AI verifier. No Closed → New in this mode.',
    ),
  },
  c22: {
    title: t('Kontaktaufnahme: Namen + vollständige E-Mail', 'Contact capture: names + complete email'),
    basis: t(
      'Vorname, Nachname und E-Mail werden vollständig aufgenommen und bestätigt. Auch eine wiederverwendete CRM-Kontaktperson erhält dadurch keinen authentifizierten Zugriff auf bestehende Tickets.',
      'First name, surname and email are fully captured and confirmed. Even a reused CRM contact does not thereby gain authenticated access to existing tickets.',
    ),
    email: t(
      'Die E-Mail gehört zur Kontaktaufnahme und Prüfung auf Wiederverwendung. Nur bei einer wirklich neuen Person ist zusätzlich die Einwilligung für die neuen Angaben erforderlich.',
      'Email is part of contact capture and reuse checking. Additional consent for new details is required only for a genuinely new person.',
    ),
    recovery: t(
      'Eigene Angaben korrigieren und erneut bestätigen. Identitätskollision oder fehlende erforderliche Einwilligung → Mitarbeiter. Wiederverwendung ist kein Zusammenführen von CRM-Einträgen.',
      'Correct and reconfirm supplied details. Identity collision or missing required consent → colleague. Reuse does not merge CRM records.',
    ),
  },
  'closed-new': {
    title: t('Geprüfte Identität wird übernommen', 'Verified identity is inherited'),
    basis: t(
      'Die abgeschlossene Prüfung des geschlossenen CAS bleibt für dieselbe Person, Firma und Maschine gültig. Vor- und Nachname werden nicht nochmals abgefragt.',
      'Completed verification of the closed CAS remains valid for the same person, company and machine. First name and surname are not requested again.',
    ),
    email: t(
      'Keine neue E-Mail-Prüfung. Erforderlich sind ein ausdrücklicher Wunsch nach einem neuen Ticket und eine frische gemeinsame Bestätigung von Maschine und Firma.',
      'No new email verification. An explicit new-ticket request and fresh combined confirmation of machine and company are required.',
    ),
    recovery: t(
      'Bei fehlgeschlagener Vorbereitung bleiben alter CAS und Identität unverändert. Erneuter Versuch, Abbruch oder Mitarbeiter nach passender Klärung; keine neue Authentifizierung als Umweg.',
      'If preparation fails, the old CAS and identity remain unchanged. Retry, cancel or choose a colleague after the appropriate clarification; do not restart authentication as a workaround.',
    ),
  },
  technician: {
    title: t('Technikerberechtigung + Kundenkontakt', 'Engineer permission + customer contact'),
    basis: t(
      'Die eingehende Nummer erkennt den registrierten Techniker. Die Kundenkontaktwahl bleibt getrennt: eine eindeutige zulässige Person mit starkem Vor- und Nachnamen kann ohne Kunden-E-Mail genügen.',
      'The registered engineer is recognized by incoming phone. Customer-contact selection is separate: one unambiguous eligible person with both names matching strongly can suffice without customer email.',
    ),
    email: t(
      'Keine eigene Techniker-E-Mail. Kunden-E-Mail nur zur Klärung akzeptierter, aber schwacher oder mehrdeutiger Kundennamen; eigene Auswahlregeln, kein KI-E-Mail-Prüfer.',
      'No challenge of the engineer’s own email. Customer email clarifies accepted but weak or ambiguous customer names, using separate selection rules and no AI email verifier.',
    ),
    recovery: t(
      'Kundennamenfehler haben eigene Korrekturregeln. Nicht auflösbare Kontaktwahl → Mitarbeiter. Kein neuer Kundenkontakt und kein Zugriff auf alte CAS.',
      'Customer-name mismatches follow separate correction rules. Unresolved selection → colleague. No new customer contact or access to old cases.',
    ),
  },
};
